import type { Audience, ProjectSetup, SetupEnvelope, SourceService } from '../api/setup';
import { newReference } from './events';

export const SERVICES: { value: SourceService; label: string }[] = [
  { value: 'github', label: 'GitHub repository' },
  { value: 'discord', label: 'Discord' },
  { value: 'notion', label: 'Notion' },
  { value: 'other', label: 'Other' },
];

export interface SourceDraft {
  id: string;
  service: SourceService;
  label: string;
  reference: string;
  purpose: string;
}

export interface DestinationDraft {
  id: string;
  title: string;
  purpose: string;
  expectedContent: string;
  reference: string;
}

/** Everything the steps edit. It lives above the steps, so Back and Next never lose input. */
export interface Draft {
  title: string;
  summary: string;
  goal: string;
  successSignal: string;
  sources: SourceDraft[];
  destinations: DestinationDraft[];
  audience: Audience;
}

export type Errors = Record<string, string>;
export type Mode = 'create' | 'edit';

export const emptyDraft = (): Draft => ({ title: '', summary: '', goal: '', successSignal: '', sources: [], destinations: [], audience: 'private' });

export const newSource = (): SourceDraft => ({ id: newReference(), service: 'github', label: '', reference: '', purpose: '' });

export const newDestination = (): DestinationDraft => ({ id: newReference(), title: '', purpose: '', expectedContent: '', reference: '' });

/** Mirrors the service's input bounds, so a long entry is stopped while typing rather than refused on save. */
export const LIMITS = { title: 500, summary: 5000, goal: 10000, successSignal: 5000, label: 200, purpose: 2000, reference: 2048, entries: 20 } as const;

/**
 * Mirrors the service's reference rule: a plain public https link with no
 * credentials, query or fragment. References are catalog metadata, never fetched.
 */
export function isSafeHttps(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const ipLike = host.includes(':') || host.startsWith('[') || /^[\d.]+$/.test(host);
    return (
      url.protocol === 'https:' &&
      url.username === '' &&
      url.password === '' &&
      url.search === '' &&
      url.hash === '' &&
      !ipLike &&
      host.includes('.') &&
      !host.endsWith('.') &&
      !/(^|\.)(localhost|local|internal|lan)$/.test(host)
    );
  } catch {
    return false;
  }
}

/**
 * Opens an existing project's setup. An unconfigured pilot starts with its
 * registered repository as a suggested source and an empty goal: nothing is
 * marked confirmed on the owner's behalf.
 */
export function draftFromSetup(title: string, envelope: Pick<SetupEnvelope, 'setup'> | undefined, repository?: string): Draft {
  const setup = envelope?.setup ?? null;
  if (setup === null) {
    return { ...emptyDraft(), title, sources: repository !== undefined ? [{ ...newSource(), label: 'Repository', reference: repository }] : [] };
  }
  return {
    title,
    summary: '',
    goal: setup.goal,
    successSignal: setup.successSignal ?? '',
    sources: setup.sources.map((source) => ({
      id: source.id,
      service: source.service,
      label: source.label,
      reference: source.reference ?? '',
      purpose: source.purpose,
    })),
    destinations: setup.destinations.map((destination) => ({
      id: destination.id,
      title: destination.title,
      purpose: destination.purpose,
      expectedContent: destination.expectedContent,
      reference: destination.reference ?? '',
    })),
    audience: 'private',
  };
}

export function stepErrors(step: number, draft: Draft, mode: Mode): Errors {
  const errors: Errors = {};
  const reference = (value: string, key: string) => {
    if (value.trim() !== '' && !isSafeHttps(value.trim())) errors[key] = 'Use a public https:// link without a ? query or # fragment, or leave it empty.';
  };
  if (step === 0) {
    if (mode === 'create' && draft.title.trim() === '') errors.title = 'Give the project a name.';
    if (draft.goal.trim() === '') errors.goal = 'Say what you want to learn, solve or change.';
  }
  if (step === 1) {
    draft.sources.forEach((source, index) => {
      if (source.label.trim() === '') errors[`sources.${String(index)}.label`] = 'Name this source, or remove it.';
      if (source.purpose.trim() === '') errors[`sources.${String(index)}.purpose`] = 'Say what this source is for, or remove it.';
      reference(source.reference, `sources.${String(index)}.reference`);
    });
  }
  if (step === 2) {
    draft.destinations.forEach((destination, index) => {
      if (destination.title.trim() === '') errors[`destinations.${String(index)}.title`] = 'Name this destination, or remove it.';
      if (destination.purpose.trim() === '') errors[`destinations.${String(index)}.purpose`] = 'Say what this destination is for.';
      if (destination.expectedContent.trim() === '') errors[`destinations.${String(index)}.expectedContent`] = 'Say what kind of content belongs here.';
      reference(destination.reference, `destinations.${String(index)}.reference`);
    });
  }
  return errors;
}

/** The first step with a problem, so a final save can send the owner back to it. */
export function firstInvalidStep(draft: Draft, mode: Mode): { step: number; errors: Errors } | null {
  for (let step = 0; step < 3; step++) {
    const errors = stepErrors(step, draft, mode);
    if (Object.keys(errors).length > 0) return { step, errors };
  }
  return null;
}

export function toSetup(draft: Draft): ProjectSetup {
  return {
    goal: draft.goal.trim(),
    ...(draft.successSignal.trim() !== '' ? { successSignal: draft.successSignal.trim() } : {}),
    sources: draft.sources.map((source) => ({
      id: source.id,
      service: source.service,
      label: source.label.trim(),
      ...(source.reference.trim() !== '' ? { reference: source.reference.trim() } : {}),
      purpose: source.purpose.trim(),
    })),
    destinations: draft.destinations.map((destination) => ({
      id: destination.id,
      title: destination.title.trim(),
      purpose: destination.purpose.trim(),
      expectedContent: destination.expectedContent.trim(),
      ...(destination.reference.trim() !== '' ? { reference: destination.reference.trim() } : {}),
    })),
  };
}

/** Compares saved and attempted setups regardless of key order in the response. */
export function sameSetup(saved: ProjectSetup | null | undefined, attempted: ProjectSetup): boolean {
  if (saved === null || saved === undefined) return false;
  return JSON.stringify(toSetup(draftFromSetup('', { setup: saved }))) === JSON.stringify(toSetup(draftFromSetup('', { setup: attempted })));
}
